import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import postSitePingNow, { PingNowResponse } from "../http/postSitePingNow";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface Args {
  siteId: string;
  esysUuid?: string;
}

/**
 * Synchronously triggers one extra Soracom downlink ping for the given
 * site (optionally narrowed to one esys). On success, invalidates the
 * ping-latest tile feed and the ping-history chart series for the
 * site so the modal refreshes with the new row.
 *
 * Wall-clock: the ping itself takes ~5-12s (5 ICMP echoes with a 2s
 * timeout each, plus a Soracom-side round trip). The Lambda runs sync
 * through API Gateway; that fits inside the 29s integration timeout
 * with ample headroom.
 */
const useSitePingNow = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useMutation<PingNowResponse, unknown, Args>(
    async ({ siteId, esysUuid }) =>
      postSitePingNow(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        esysUuid
      ),
    {
      onSuccess: (_data, { siteId }) => {
        queryClient.invalidateQueries(["site", siteId, "ping-latest"]);
        // Chart-data keys are `["site", siteId, "chart-data", dataType, ...]`
        // - invalidate everything under the site so the ping-history
        // ECharts re-fetches its current window without the modal
        // having to know the exact query-key shape.
        queryClient.invalidateQueries(["site", siteId, "chart-data"]);
      },
    }
  );
};

export default useSitePingNow;
